import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useFeatures } from './FeaturesContext';

/**
 * L'état de la caisse du jour, partagé par le tableau de bord et le portefeuille.
 *
 * Deux problèmes se réglaient ici.
 *
 * Le premier : chaque écran interrogeait le serveur de son côté et jetait le
 * résultat en se démontant. Revenir sur Comptabilité repartait donc de zéro, et
 * l'utilisateur voyait son état de caisse se construire sous ses yeux à chaque
 * navigation. Le contexte vit au-dessus des écrans : au retour, la valeur est
 * déjà là.
 *
 * Le second, plus insidieux : l'état initial affirmait `non_ouverte`. L'inconnu
 * et le « pas ouvert » étaient représentés à l'identique, si bien qu'un écran en
 * cours de chargement annonçait une caisse fermée à quelqu'un dont la caisse
 * était ouverte. Ici, `statut` vaut `null` tant qu'on ne sait pas — et
 * l'ignorance se lit comme telle.
 *
 * Ce contexte ne décrit QUE la journée en cours. Le portefeuille, qui permet de
 * naviguer dans les dates passées, interroge le serveur directement pour les
 * autres jours.
 */
const CaisseContext = createContext(null);

export const useCaisse = () => {
  const contexte = useContext(CaisseContext);
  if (!contexte) {
    throw new Error('useCaisse doit être utilisé dans un CaisseProvider');
  }
  return contexte;
};

/** La date du jour au format attendu par l'API, dans le fuseau local. */
const dateDuJour = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Au-delà de ce délai sans que la fenêtre ait le focus, on revérifie au retour.
// Couvre le cas d'un onglet laissé de côté pendant que la caisse est ouverte
// depuis un autre appareil — le seul écart réellement possible, la caisse étant
// propre à chaque gestionnaire.
const DELAI_REVALIDATION_MS = 60 * 1000;

export const CaisseProvider = ({ children }) => {
  // La caisse du jour appartient au portefeuille, donc a la comptabilite. Sans
  // ce module, `/api/portefeuille` refuse en 403 — a juste titre — mais le
  // contexte interrogeait quand meme, a chaque montage et toutes les minutes.
  // Une ecole sans comptabilite voyait donc defiler des erreurs pour un ecran
  // qu'elle n'a pas.
  const { estActive } = useFeatures();
  const comptabiliteOuverte = estActive('module.comptabilite');

  const [statut, setStatut] = useState(null);
  const [charge, setCharge] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [anneeActive, setAnneeActive] = useState(null);
  const [date, setDate] = useState(dateDuJour());

  // Évite deux appels simultanés et garde la trace du dernier succès.
  const enCours = useRef(false);
  const dernierChargement = useRef(0);

  const rafraichir = useCallback(async ({ silencieux = false } = {}) => {
    if (!comptabiliteOuverte || !anneeActive?.id || enCours.current) return;
    enCours.current = true;
    if (!silencieux) setErreur(null);

    const jour = dateDuJour();
    try {
      const reponse = await api.portefeuille.getStatutCloture(jour, anneeActive.id);
      if (reponse?.success) {
        setStatut(reponse.data);
        setDate(jour);
        setCharge(true);
        dernierChargement.current = Date.now();
      } else if (!silencieux) {
        setErreur(reponse?.error || "L'état de la caisse n'a pas pu être lu.");
      }
    } catch (e) {
      if (!silencieux) setErreur(e.message || "L'état de la caisse n'a pas pu être lu.");
    } finally {
      enCours.current = false;
    }
  }, [anneeActive?.id, comptabiliteOuverte]);

  // L'année active : le contexte la charge lui-même pour ne dépendre d'aucun
  // écran, et rester disponible quel que soit le module affiché.
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const reponse = await api.anneesScolaires.getActive();
        if (!annule && reponse?.success && reponse.data) setAnneeActive(reponse.data);
      } catch (e) {
        // Sans année active il n'y a pas de caisse à décrire : `statut` reste
        // nul, et les écrans continuent d'afficher « Vérification… ».
      }
    })();
    return () => { annule = true; };
  }, []);

  useEffect(() => { rafraichir(); }, [rafraichir]);

  // Retour sur l'onglet après une absence : on revérifie sans rien effacer, pour
  // que l'écran ne clignote pas si rien n'a changé.
  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState !== 'visible') return;
      const ecoule = Date.now() - dernierChargement.current;
      if (ecoule > DELAI_REVALIDATION_MS) rafraichir({ silencieux: true });
    };
    document.addEventListener('visibilitychange', auRetour);
    window.addEventListener('focus', auRetour);
    return () => {
      document.removeEventListener('visibilitychange', auRetour);
      window.removeEventListener('focus', auRetour);
    };
  }, [rafraichir]);

  // Passage de minuit : la caisse du jour n'est plus la même.
  useEffect(() => {
    const verifierJour = () => {
      if (dateDuJour() !== date) {
        setStatut(null);
        setCharge(false);
        rafraichir();
      }
    };
    const minuteur = setInterval(verifierJour, 60 * 1000);
    return () => clearInterval(minuteur);
  }, [date, rafraichir]);

  const valeur = {
    statut,
    charge,
    erreur,
    date,
    anneeActive,
    rafraichir,
    // Trois lectures dérivées, pour que les écrans n'aient pas à réinventer la
    // distinction entre « je ne sais pas » et « ce n'est pas ouvert ».
    inconnu: !charge,
    estOuverte: charge && (statut?.est_ouverte === true || statut?.statut === 'ouverte'),
    estCloturee: charge && statut?.est_cloturee === true,
    caissePrecedenteNonCloturee: charge ? (statut?.caisse_precedente_non_cloturee || null) : null
  };

  return <CaisseContext.Provider value={valeur}>{children}</CaisseContext.Provider>;
};

export default CaisseContext;
